/**
 * Owner: Masa Bayu
 * File: author-account.ts
 * Date:Time: 2026-09-16 13:43
 * Description: T6-E1 AuthorAccount — links Account + public profile; own-resource.
 */
import { assertNoForbiddenFoundationKey } from './authority';
import { assertT6E1SecurityIntact } from './author-security';
import { createQxreadId } from './id';
import { createQxreadAccount, deriveChromeMode, isSessionAuthenticated, } from './identity';
import { assertOptionalTrimmed, assertTimestamp, assertValidId } from './guards';
export const QXREAD_AUTHOR_ACCOUNT_STATES = ['pending', 'active', 'suspended'];
function assertAuthorState(value, label) {
    if (typeof value !== 'string' ||
        !QXREAD_AUTHOR_ACCOUNT_STATES.includes(value)) {
        throw new Error(`${label} must be one of: ${QXREAD_AUTHOR_ACCOUNT_STATES.join(', ')}`);
    }
    return value;
}
function assertSlug(value, label) {
    if (typeof value !== 'string' || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value)) {
        throw new Error(`${label} must be a lowercase slug`);
    }
    return value;
}
/**
 * Create an AuthorAccount bound to an Account. Roles alone do not invent this record.
 */
export function createQxreadAuthorAccount(options) {
    assertNoForbiddenFoundationKey(options, 'QXRead author account');
    assertT6E1SecurityIntact();
    assertValidId(options.accountId, 'QXRead author accountId');
    const displayName = assertOptionalTrimmed(options.displayName, 'QXRead author displayName');
    if (displayName === null) {
        throw new Error('QXRead author displayName is required');
    }
    const slug = assertSlug(options.slug, 'QXRead author slug');
    const createdAt = options.createdAt ?? new Date().toISOString();
    assertTimestamp(createdAt, 'QXRead author createdAt');
    const updatedAt = options.updatedAt ?? createdAt;
    assertTimestamp(updatedAt, 'QXRead author updatedAt');
    const authorAccountId = options.authorAccountId !== undefined ? options.authorAccountId : createQxreadId('qxrdauth');
    assertValidId(authorAccountId, 'QXRead authorAccountId');
    return Object.freeze({
        authorAccountId,
        accountId: options.accountId,
        publicProfile: Object.freeze({
            displayName,
            slug,
            bio: assertOptionalTrimmed(options.bio, 'QXRead author bio'),
        }),
        state: assertAuthorState(options.state ?? 'active', 'QXRead author state'),
        createdAt,
        updatedAt,
    });
}
/**
 * Own-resource scope: authenticated session Account must own the AuthorAccount.
 * Chrome mode is derived for UI only — never security authority alone.
 */
export function authorizeAuthorSurfaceAccess(input) {
    assertNoForbiddenFoundationKey(input, 'QXRead author surface access');
    assertT6E1SecurityIntact();
    const chromeMode = deriveChromeMode(input.account.roles);
    if (!isSessionAuthenticated(input.session)) {
        return Object.freeze({
            allowed: false,
            reason: 'Author surface requires authenticated session',
            chromeMode,
        });
    }
    if (input.session.accountId !== input.account.accountId) {
        return Object.freeze({
            allowed: false,
            reason: 'Session accountId does not match Account',
            chromeMode,
        });
    }
    if (input.account.accountId !== input.authorAccount.accountId) {
        return Object.freeze({
            allowed: false,
            reason: 'Own-resource scope denied — AuthorAccount belongs to another Account',
            chromeMode,
        });
    }
    if (!input.account.roles.includes('author') && !input.account.roles.includes('admin')) {
        return Object.freeze({
            allowed: false,
            reason: 'Account lacks author role for Author Platform chrome',
            chromeMode,
        });
    }
    if (input.authorAccount.state === 'suspended') {
        return Object.freeze({
            allowed: false,
            reason: 'AuthorAccount is suspended',
            chromeMode,
        });
    }
    return Object.freeze({
        allowed: true,
        reason: 'Author surface authorized under own-resource scope',
        chromeMode,
    });
}
/** Helper for demos — account with author role. */
export function createAuthorCapableAccount(options) {
    return createQxreadAccount({
        ...(options.accountId !== undefined ? { accountId: options.accountId } : {}),
        ...(options.displayName !== undefined ? { displayName: options.displayName } : {}),
        roles: ['reader', 'author'],
    });
}
//# sourceMappingURL=author-account.js.map